// Referrals widget — the $250 program's two live jobs (Ship 3, Aug 26 2026).
//
//   1. A referral nobody has called yet. The whole program dies here — the
//      program went live Aug 20 and the first real one landed Aug 25.
//   2. A fee that is owed. `payout_status IN ('earned','approved')` is NOT
//      "money owed" on its own: a duplicate claim deliberately sits at
//      'earned' until Chad voids it, so every total must ALSO filter
//      `superseded_by_referral_id IS NULL` (the Aug 20 rule). Duplicates are
//      counted separately because voiding them is Chad's specific job.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { daysSince, money } from "../../../lib/referrals";
import { logCardClick } from "../../../lib/uiEvents";

const SHOW = 3;

interface Row {
  id: string;
  referred_name: string;
  referred_city: string | null;
  status: string;
  created_at: string;
}

interface View {
  toCall: Row[];
  owedCount: number;
  owedAmount: number;
  toApprove: number;
  toPay: number;
  duplicates: number;
}

export default function ReferralsWidget() {
  const [view, setView] = useState<View | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Paged + id-ordered: the fees figure below is a MONEY total, and a
      // silent truncation at the 1,000-row cap would under-report it.
      type Raw = Row & {
        payout_status: string;
        payout_amount: number | null;
        superseded_by_referral_id: string | null;
      };
      const PAGE = 1000;
      const rows: Raw[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await supabase
          .from("referrals")
          .select(
            "id,referred_name,referred_city,status,created_at,payout_status,payout_amount,superseded_by_referral_id",
          )
          .order("id")
          .range(from, from + PAGE - 1);
        if (cancelled) return;
        if (error || !data) {
          setFailed(true);
          return;
        }
        rows.push(...(data as Raw[]));
        if (data.length < PAGE) break;
      }

      // Oldest first — a referral sitting four days is the one to call.
      const toCall = rows
        .filter((r) => r.status === "new" || r.status === "contacted")
        .sort((a, b) => a.created_at.localeCompare(b.created_at));

      const live = rows.filter(
        (r) =>
          (r.payout_status === "earned" || r.payout_status === "approved") &&
          r.superseded_by_referral_id === null,
      );
      setView({
        toCall,
        owedCount: live.length,
        owedAmount: live.reduce((s, r) => s + Number(r.payout_amount ?? 0), 0),
        toApprove: live.filter((r) => r.payout_status === "earned").length,
        toPay: live.filter((r) => r.payout_status === "approved").length,
        duplicates: rows.filter(
          (r) => r.superseded_by_referral_id !== null && r.payout_status !== "void",
        ).length,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🤝</span>
        <span className="font-bold text-sm text-bardin-ink">Referrals</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {failed ? "—" : view ? `${view.toCall.length} to call` : "…"}
        </span>
      </div>

      {failed ? (
        <div className="text-xs text-slate-400 py-2">Couldn't load referrals — try a refresh.</div>
      ) : !view ? (
        <div className="space-y-2 mt-1">
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        </div>
      ) : (
        <>
          {view.toCall.length === 0 ? (
            <div className="text-[11px] text-emerald-700 py-1.5 font-semibold">
              Nobody's waiting on a call ✓
            </div>
          ) : (
            <>
              <div className="divide-y divide-slate-100">
                {view.toCall.slice(0, SHOW).map((r) => {
                  const d = daysSince(r.created_at);
                  return (
                    <Link
                      key={r.id}
                      to={`/referrals?tab=queue&focus=${r.id}`}
                      onClick={() => logCardClick("widget_referrals_row")}
                      className="flex items-center gap-2 py-1.5"
                    >
                      <span className="text-[13px] font-bold text-bardin-ink truncate">
                        {r.referred_name}
                      </span>
                      <span className="text-[11px] text-slate-400 truncate flex-1">
                        {r.referred_city ?? ""}
                      </span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-extrabold whitespace-nowrap ${
                          (d ?? 0) >= 3 ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {r.status === "new" ? "New" : "Contacted"}
                        {d != null ? ` · ${d}d` : ""}
                      </span>
                    </Link>
                  );
                })}
              </div>
              {view.toCall.length > SHOW && (
                <div className="text-[10px] text-slate-400 font-semibold mt-0.5">
                  +{view.toCall.length - SHOW} more
                </div>
              )}
            </>
          )}

          <Link
            to="/referrals?tab=payouts"
            onClick={() => logCardClick("widget_referrals_fees")}
            className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-bold text-bardin-ink">Fees outstanding</div>
              <div className="text-[10.5px] text-slate-400">
                {view.owedCount === 0
                  ? "nothing owed right now"
                  : `${view.toApprove} to approve · ${view.toPay} to pay`}
              </div>
            </div>
            <span
              className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold ${
                view.owedCount > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-400"
              }`}
            >
              {money(view.owedAmount)}
            </span>
          </Link>

          {view.duplicates > 0 && (
            <div className="text-[10px] text-amber-700 font-semibold mt-1.5">
              ⚠ {view.duplicates} duplicate claim{view.duplicates === 1 ? "" : "s"} to review and void —
              excluded from the total above.
            </div>
          )}
        </>
      )}
    </div>
  );
}
